import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  CardActions,
  Button,
  useTheme
} from '@mui/material';
import {
  FaUsers,
  FaBuilding,
  FaLaptopCode,
  FaMoneyBillWave,
  FaShieldAlt,
  FaBullhorn
} from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';

const NdisOperations: React.FC = () => {
  const theme = useTheme();
  const tabConfigs = [
    {
      label: 'People & Culture',
      icon: <FaUsers />,
      path: 'people-culture',
      color: '#4e79a7',
      description: 'Manage workforce development and culture'
    },
    {
      label: 'Corporate Services',
      icon: <FaBuilding />,
      path: 'corporate-services',
      color: '#f28e2b',
      description: 'Oversee corporate operations'
    },
    {
      label: 'IT',
      icon: <FaLaptopCode />,
      path: 'information-technology',
      color: '#e15759',
      description: 'IT infrastructure and solutions'
    },
    {
      label: 'Finance',
      icon: <FaMoneyBillWave />,
      path: 'finance',
      color: '#76b7b2',
      description: 'Financial operations'
    },
    {
      label: 'Risk & Compliance',
      icon: <FaShieldAlt />,
      path: 'risk-compliance',
      color: '#59a14f',
      description: 'Regulatory compliance'
    },
    {
      label: 'Marketing',
      icon: <FaBullhorn />,
      path: 'marketing',
      color: '#edc948',
      description: 'Brand awareness'
    }
  ];

  const navigate = useNavigate();
  const location = useLocation();

  const isActivePath = (path: string) => {
    return location.pathname === `/ndis-operations/${path}`;
  };

  const handleCardClick = (path: string) => {
    navigate(`/ndis-operations/${path}`);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Compact Header */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          mb: { xs: 2, sm: 3 },
          textAlign: 'center',
          color: theme.palette.primary.dark
        }}
      >
        NDIS Operations
      </Typography>

      {/* Responsive Cards Grid */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {tabConfigs.map((tab, index) => {
          const isActive = isActivePath(tab.path);

          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  background: isActive ? alpha(tab.color, 0.08) : '#ffffff',
                  border: `1px solid ${isActive ? tab.color : '#e0e0e0'}`,
                  boxShadow: 'none',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: tab.color,
                    boxShadow: `0 4px 8px ${alpha(tab.color, 0.15)}`
                  }
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 3 }
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(tab.color, 0.1),
                      color: tab.color,
                      width: { xs: 48, sm: 56 },
                      height: { xs: 48, sm: 56 },
                      mb: { xs: 1.5, sm: 2 },
                      border: `1px solid ${tab.color}`
                    }}
                  >
                    {tab.icon}
                  </Avatar>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      fontSize: { xs: '0.95rem', sm: '1rem' }
                    }}
                  >
                    {tab.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      px: { xs: 0.5, sm: 1 }
                    }}
                  >
                    {tab.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: { xs: 1, sm: 1.5 } }}>
                  <Button
                    variant={isActive ? 'contained' : 'outlined'}
                    fullWidth
                    size="small"
                    sx={{
                      color: isActive ? '#fff' : tab.color,
                      bgcolor: isActive ? tab.color : 'transparent',
                      borderColor: tab.color,
                      fontWeight: 500,
                      py: { xs: 0.8, sm: 1 },
                      borderRadius: 1.5,
                      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                      '&:hover': {
                        bgcolor: isActive ? tab.color : alpha(tab.color, 0.1)
                      }
                    }}
                    onClick={() => handleCardClick(tab.path)}
                  >
                    {isActive ? 'Viewing' : 'View'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default NdisOperations;
